import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthFilePipe } from '@shared/pipes/authFile.pipe';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import {
  AttachmentLibraryGroupName,
  ProjectAttachmentLibraryGroup,
  ProjectAttachmentLibraryItem
} from '../../models/project-attachment-library.model';
import { ProjectAttachmentLibraryService } from '../../services/project-attachment-library.service';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { FilePreviewerComponent } from '@shared/components/file-previewer/file-previewer.component';
import { Attachment } from '@shared/interfaces/attachment.interface';
import { FileSignatureController } from '@shared/controllers/file-signature.controller';
import { getPrivateAttachmentUrl } from '@shared/helpers/private-attachment-url.helper';
import { getAttachmentSelectionRangeIds } from '@shared/helpers/attachment-selection.helper';

type AttachmentLibraryTab = 'all' | AttachmentLibraryGroupName;

interface GroupState extends ProjectAttachmentLibraryGroup {
  page: number;
  loading: boolean;
  initialized: boolean;
}

export interface AttachmentLibraryDialogData {
  projectId: number | null;
  attachmentsConfig: TextAttachmentsConfig;
  attachedIds: number[];
  maxSelectable: number | null;
}

export interface AttachmentLibraryDialogResult {
  sourceAttachmentIds: number[];
  items: ProjectAttachmentLibraryItem[];
}

const GROUPS: AttachmentLibraryGroupName[] = ['photos', 'videos', 'documents'];

const emptyGroup = (): GroupState => ({ records: [], total: 0, page: 0, loading: false, initialized: false });

@Component({
  selector: 'app-attachment-library-modal',
  templateUrl: './attachment-library-modal.component.html',
  styleUrls: ['./attachment-library-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachmentLibraryModalComponent {
  private readonly dialogRef = inject(MatDialogRef<AttachmentLibraryModalComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly libraryService = inject(ProjectAttachmentLibraryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fileSignatureController = inject(FileSignatureController);
  protected readonly data = inject<AttachmentLibraryDialogData>(MAT_DIALOG_DATA);
  protected readonly authFile = inject(AuthFilePipe);
  protected readonly projectForm = new UntypedFormGroup({
    projectId: new UntypedFormControl(this.data.projectId || null)
  });

  protected readonly groups = signal<Record<AttachmentLibraryGroupName, GroupState>>({
    photos: emptyGroup(),
    videos: emptyGroup(),
    documents: emptyGroup()
  });
  private readonly allFiles = signal<Array<{ item: ProjectAttachmentLibraryItem; group: AttachmentLibraryGroupName }>>([]);
  protected readonly activeTab = signal<AttachmentLibraryTab>('all');
  protected readonly selectedIds = signal<Set<number>>(new Set());
  private readonly selectedItems = signal<Map<number, ProjectAttachmentLibraryItem>>(new Map());
  private readonly selectionAnchorId = signal<number | null>(null);
  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly viewMode = signal<'grid' | 'list'>('grid');
  protected readonly initialLoading = signal(true);
  protected readonly requestVersion = signal(0);
  protected readonly groupNames = GROUPS;
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTypes = ButtonTypes;

  constructor() {
    this.dialogRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') this.onEscape();
      });

    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((search) => this.loadFirstPages(search.trim()));

    this.projectForm
      .get('projectId')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadFirstPages(this.searchControl.value.trim()));
  }

  protected setActiveTab(tab: AttachmentLibraryTab): void {
    this.activeTab.set(tab);
    if (tab === 'all') {
      this.loadAllGroups(this.searchControl.value.trim());
      return;
    }

    this.reloadGroup(tab);
  }

  protected getGroup(group: AttachmentLibraryGroupName): GroupState {
    return this.groups()[group];
  }

  protected getTabTotal(tab: AttachmentLibraryTab): number {
    if (tab === 'all') return GROUPS.reduce((total, group) => total + this.getGroup(group).total, 0);
    return this.getGroup(tab).total;
  }

  protected get selectedCount(): number {
    return this.selectedIds().size;
  }

  protected get selectedSize(): number {
    const selected = this.selectedIds();
    return GROUPS.reduce(
      (sum, group) => sum + this.getGroup(group).records.filter((item) => selected.has(item.id)).reduce((s, item) => s + item.size, 0),
      0
    );
  }

  protected toggleSelection(
    item: ProjectAttachmentLibraryItem,
    group: AttachmentLibraryGroupName,
    event: MouseEvent
  ): void {
    event.preventDefault();
    event.stopPropagation();
    const selected = new Set(this.selectedIds());
    const anchorId = this.selectionAnchorId();
    if (event.shiftKey && anchorId !== null) {
      const entries = this.getSelectionEntries();
      const rangeIds = getAttachmentSelectionRangeIds(entries.map((entry) => entry.item), anchorId, item.id);
      if (rangeIds.length) {
        const entriesById = new Map(entries.map((entry) => [entry.item.id, entry]));
        const selectedItems = new Map(this.selectedItems());
        rangeIds.forEach((id) => {
          const entry = entriesById.get(id);
          if (entry && this.canSelect(entry.item, entry.group, selected)) {
            selected.add(id);
            selectedItems.set(id, entry.item);
          }
        });
        this.selectedIds.set(selected);
        this.selectedItems.set(selectedItems);
        return;
      }
    }

    const selectedItems = new Map(this.selectedItems());
    if (selected.has(item.id)) {
      selected.delete(item.id);
      selectedItems.delete(item.id);
      if (anchorId === item.id) this.selectionAnchorId.set(null);
    } else if (this.canSelect(item, group, selected)) {
      selected.add(item.id);
      selectedItems.set(item.id, item);
      this.selectionAnchorId.set(item.id);
    }
    this.selectedIds.set(selected);
    this.selectedItems.set(selectedItems);
  }

  protected isSelected(item: ProjectAttachmentLibraryItem): boolean {
    return this.selectedIds().has(item.id);
  }

  protected isSelectable(item: ProjectAttachmentLibraryItem, group: AttachmentLibraryGroupName): boolean {
    return this.canSelect(item, group, this.selectedIds());
  }

  protected onContentScroll(event: Event): void {
    const element = event.target as HTMLElement;
    if (element.scrollTop + element.clientHeight < element.scrollHeight - 280) return;

    const activeTab = this.activeTab();
    if (activeTab === 'all') {
      GROUPS.forEach((group) => this.loadNextPage(group, true));
      return;
    }
    this.loadNextPage(activeTab);
  }

  protected setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode.set(mode);
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected onEscape(): void {
    if (document.querySelector('.fancybox-container')) return;
    if (this.selectedCount) {
      this.clearSelection();
      return;
    }

    this.close();
  }

  protected submit(): void {
    const sourceAttachmentIds = [...this.selectedIds()];
    if (!sourceAttachmentIds.length) return;
    const items = sourceAttachmentIds
      .map((id) => this.selectedItems().get(id))
      .filter((item): item is ProjectAttachmentLibraryItem => !!item);
    this.dialogRef.close({ sourceAttachmentIds, items } satisfies AttachmentLibraryDialogResult);
  }

  protected fileSize(size: number): string {
    if (!size) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
    return `${(size / Math.pow(1024, index)).toFixed(index ? 1 : 0)} ${units[index]}`;
  }

  protected sourceLabel(item: ProjectAttachmentLibraryItem): string | null {
    if (!item.source) return null;
    const labels: Record<string, string> = {
      task: 'Aufgabe',
      comment: 'Kommentar',
      'protocol-entry': 'Protokoll',
      project_entry_wysiwyg: 'Projekteintrag',
      project_description_wysiwyg: 'Projektbeschreibung',
      site_diary: 'Bautagebuch'
    };
    return `${labels[item.source.type] || item.source.type} · ${item.source.name}`;
  }

  protected trackItem(_index: number, item: ProjectAttachmentLibraryItem): number {
    return item.id;
  }

  protected getAllFiles(): Array<{ item: ProjectAttachmentLibraryItem; group: AttachmentLibraryGroupName }> {
    return this.allFiles();
  }

  protected thumbnailUrl(item: ProjectAttachmentLibraryItem): string | undefined {
    if (item.isVideo && item.hasPoster) {
      return getPrivateAttachmentUrl(item.file, this.fileSignatureController, {
        poster: true,
        width: 260,
        height: 180
      });
    }
    return this.authFile.transform(item.file, 260, 180);
  }

  protected preview(item: ProjectAttachmentLibraryItem): void {
    const source = this.authFile.transform(item.file);
    if (!source) return;
    if (item.isImage) {
      ($ as any).fancybox.open([{ src: source, type: 'image', opts: { caption: item.name } }]);
      return;
    }
    if (item.type?.toLowerCase?.().includes('pdf')) {
      // Keep PDF previews consistent with the project gallery (fancybox iframe viewer)
      // instead of opening the legacy document-preview dialog.
      ($ as any).fancybox.open([{ src: source, type: 'iframe', opts: { caption: item.name } }]);
      return;
    }
    this.dialog.open(FilePreviewerComponent, {
      panelClass: 'file-previewer-modal',
      height: '100vh',
      width: '100vw',
      autoFocus: false,
      data: {
        file: new Attachment(item as unknown as Attachment),
        downloadFunc: () => window.open(source, '_blank'),
        isPDF: item.type === 'application/pdf',
        isVideo: item.isVideo
      }
    });
  }

  private loadFirstPages(search: string): void {
    this.loadAllGroups(search);
  }

  private loadAllGroups(search: string): void {
    const version = this.requestVersion() + 1;
    this.requestVersion.set(version);
    this.initialLoading.set(true);
    this.groups.set({ photos: emptyGroup(), videos: emptyGroup(), documents: emptyGroup() });
    this.allFiles.set([]);

    this.libraryService.getLibrary({
      projectIds: this.selectedProjectIds(),
      search,
      page: 1,
      records: 30
    }).subscribe({
      next: (response) => {
        if (version !== this.requestVersion()) return;
        const groups =
          GROUPS.reduce((state, group) => {
            const result = response?.[group] || { records: [], total: 0 };
            state[group] = { records: result.records || [], total: result.total || 0, page: 1, loading: false, initialized: true };
            return state;
          }, {} as Record<AttachmentLibraryGroupName, GroupState>);
        this.groups.set(groups);
        this.allFiles.set(
          GROUPS.reduce(
            (files, group) => [...files, ...groups[group].records.map((item) => ({ item, group }))],
            [] as Array<{ item: ProjectAttachmentLibraryItem; group: AttachmentLibraryGroupName }>
          )
        );
      },
      error: () => {
        if (version !== this.requestVersion()) return;
        this.groups.set({ photos: emptyGroup(), videos: emptyGroup(), documents: emptyGroup() });
        this.allFiles.set([]);
      }
    }).add(() => {
      if (version === this.requestVersion()) this.initialLoading.set(false);
    });
  }

  private loadNextPage(group: AttachmentLibraryGroupName, appendToAll = false): void {
    const state = this.getGroup(group);
    if (!state.initialized || state.loading || state.records.length >= state.total) return;

    this.updateGroup(group, { ...state, loading: true });
    const version = this.requestVersion();
    this.libraryService
      .getLibrary({
        projectIds: this.selectedProjectIds(),
        search: this.searchControl.value.trim(),
        group,
        page: state.page + 1,
        records: 30
      })
      .subscribe({
        next: (response) => {
          if (version !== this.requestVersion()) return;
          const result = response?.[group] || { records: [], total: state.total };
          const ids = new Set(state.records.map((item) => item.id));
          const addedRecords = (result.records || []).filter((item) => !ids.has(item.id));
          const records = [...state.records, ...addedRecords];
          this.updateGroup(group, { ...state, records, total: result.total ?? state.total, page: state.page + 1, loading: false });
          if (appendToAll) {
            this.allFiles.update((files) => {
              const loadedIds = new Set(files.map((entry) => entry.item.id));
              return [...files, ...addedRecords.filter((item) => !loadedIds.has(item.id)).map((item) => ({ item, group }))];
            });
          }
        },
        error: () => this.updateGroup(group, { ...state, loading: false })
      });
  }

  private reloadGroup(group: AttachmentLibraryGroupName): void {
    const current = this.getGroup(group);
    this.updateGroup(group, { ...current, records: [], page: 0, loading: true, initialized: true });

    this.libraryService
      .getLibrary({
        projectIds: this.selectedProjectIds(),
        search: this.searchControl.value.trim(),
        group,
        page: 1,
        records: 30
      })
      .subscribe({
        next: (response) => {
          const result = response?.[group] || { records: [], total: 0 };
          this.updateGroup(group, {
            records: result.records || [],
            total: result.total || 0,
            page: 1,
            loading: false,
            initialized: true
          });
        },
        error: () => this.updateGroup(group, { ...this.getGroup(group), loading: false })
      });
  }

  private updateGroup(group: AttachmentLibraryGroupName, value: GroupState): void {
    this.groups.update((groups) => ({ ...groups, [group]: value }));
  }

  private selectedProjectIds(): number[] | undefined {
    const projectId = Number(this.projectForm.get('projectId')?.value);
    return projectId > 0 ? [projectId] : undefined;
  }

  private getSelectionEntries(): Array<{ item: ProjectAttachmentLibraryItem; group: AttachmentLibraryGroupName }> {
    const activeTab = this.activeTab();
    return activeTab === 'all'
      ? this.getAllFiles()
      : this.getGroup(activeTab).records.map((item) => ({ item, group: activeTab }));
  }

  private canSelect(
    item: ProjectAttachmentLibraryItem,
    group: AttachmentLibraryGroupName,
    selected: ReadonlySet<number>
  ): boolean {
    if (this.data.attachedIds.includes(item.id)) return false;
    if (!selected.has(item.id) && selected.size >= this.selectionLimit()) return false;

    const config = this.data.attachmentsConfig;
    if (group === 'photos') return !!config.photoAttachments;
    if (group === 'videos') return !!config.videoAttachments || !!config.fileAttachments;
    return !!config.fileAttachments;
  }

  private selectionLimit(): number {
    return Math.min(200, this.data.maxSelectable ?? 200);
  }

  protected clearSelection(): void {
    this.selectedIds.set(new Set());
    this.selectedItems.set(new Map());
    this.selectionAnchorId.set(null);
  }
}
